import { useState } from 'react';
import { effectiveIntervals, type Availability, type WeeklySchedule } from '@acs/shared';
import { Badge, Button } from '../../shared/ui/index.js';
import {
  TimeGrid,
  addDays,
  labelFor,
  mondayOf,
  normalizeDay,
  todayIso,
  visibleWindow,
  type GridInterval,
  type TimeGridBlock,
  type TimeGridColumn,
} from '../schedule-grid/index.js';
import { useI18n } from '../../i18n/index.js';

/**
 * The date-overrides tab (doc 10 §4.5): the week grid over REAL dates. Each day
 * shows the *effective* schedule; the first edit on a day copies its effective
 * intervals into `schedule.exceptions[date]` and edits only that date. A
 * business-wide closure is shown locked (edit it in Settings, doc 09 D2).
 */
export function DateOverridesEditor({
  schedule,
  business,
  onChange,
}: {
  schedule: WeeklySchedule;
  business: Availability;
  onChange(next: WeeklySchedule): void;
}) {
  const { t, tag } = useI18n();
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayIso()));
  const today = todayIso();
  const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const effectiveFor = (date: string): GridInterval[] =>
    normalizeDay(effectiveIntervals({ schedule }, business, date).map((iv) => ({ ...iv })));
  const isBusinessClosed = (date: string) => business.exceptions[date]?.length === 0;
  const isOverridden = (date: string) => schedule.exceptions[date] !== undefined;
  const isPast = (date: string) => date < today;
  const locked = (date: string) => isBusinessClosed(date) || isPast(date);

  const columns: TimeGridColumn[] = dates.map((date) => ({
    id: date,
    label: labelFor(date, tag),
    readOnly: locked(date),
    badge: isBusinessClosed(date) ? (
      <Badge tone="bad">{t('overrides.closed')}</Badge>
    ) : isOverridden(date) ? (
      <Badge tone="info">{t('overrides.override')}</Badge>
    ) : undefined,
  }));

  const blocks: TimeGridBlock[] = dates.flatMap((date) =>
    effectiveFor(date).map((iv) => ({
      id: `${date}#${iv.startMin}`,
      columnId: date,
      kind: locked(date) ? ('readonly' as const) : ('editable' as const),
      ...iv,
    })),
  );

  const commit = (next: TimeGridBlock[]) => {
    const exceptions = { ...schedule.exceptions };
    for (const date of dates) {
      if (locked(date)) continue;
      const nextIvs = normalizeDay(next.filter((b) => b.columnId === date).map((b) => ({ startMin: b.startMin, endMin: b.endMin })));
      if (!sameIntervals(nextIvs, effectiveFor(date))) exceptions[date] = nextIvs;
    }
    onChange({ ...schedule, exceptions });
  };

  const setException = (date: string, ivs: GridInterval[]) =>
    onChange({ ...schedule, exceptions: { ...schedule.exceptions, [date]: normalizeDay(ivs) } });
  const resetDate = (date: string) => {
    const exceptions = { ...schedule.exceptions };
    delete exceptions[date];
    onChange({ ...schedule, exceptions });
  };

  const win = visibleWindow(dates.map(effectiveFor));

  return (
    <div className="date-overrides">
      <div className="date-overrides-pager">
        <Button variant="ghost" onClick={() => setWeekStart(addDays(weekStart, -7))}>{t('overrides.prev')}</Button>
        <span className="date-overrides-range">
          {labelFor(weekStart, tag)} – {labelFor(addDays(weekStart, 6), tag)}
        </span>
        <Button variant="ghost" onClick={() => setWeekStart(addDays(weekStart, 7))}>{t('overrides.next')}</Button>
        <Button variant="ghost" onClick={() => setWeekStart(mondayOf(today))}>{t('overrides.today')}</Button>
      </div>

      <TimeGrid columns={columns} blocks={blocks} window={win} onCommit={commit} />

      <div className="date-overrides-controls">
        {dates.map((date) => (
          <div className="date-overrides-day" key={date}>
            <span className="date-overrides-day-label">{labelFor(date, tag)}</span>
            {isBusinessClosed(date) ? (
              <span className="muted">{t('overrides.businessClosed')}</span>
            ) : isPast(date) ? (
              <span className="muted">{t('overrides.past')}</span>
            ) : (
              <>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => setException(date, [])}>
                  {t('overrides.dayOff')}
                </button>
                {isOverridden(date) && (
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => resetDate(date)}>
                    {t('overrides.resetToWeekly')}
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function sameIntervals(a: GridInterval[], b: GridInterval[]): boolean {
  return a.length === b.length && a.every((iv, i) => iv.startMin === b[i]!.startMin && iv.endMin === b[i]!.endMin);
}
